import React, { useEffect, useMemo } from 'react';
import { OpDataGrid } from 'op2mise-react-widgets/OpDataGrid';
import Switch from 'shared/components/switch/Switch';
import { Constants } from '../constants';
import { addButtonIcon, deleteIcon } from 'assets';

function BreakPatternEditorTable({
  breakPatternFormOptions,
  breakChannelTypeErrors,
  rowData,
  setDirtyForm,
  setSortingResult,
  setPatternData,
}) {
  const handleAddEditorTableRow = () => {
    // id here is used by the grid for properly rendering rows.
    const newRow = {
      ...Constants.defaultBreakPatternData,
      id: crypto.randomUUID(),
    };

    setPatternData((prevState) => ({
      ...prevState,
      breakChannelTypes: [...prevState.breakChannelTypes, newRow],
    }));
    setDirtyForm(true);
  };

  const handleDeleteRow = (params) => {
    setDirtyForm(true);
    setPatternData((prevData) => {
      const updatedRows = prevData.breakChannelTypes.filter(
        (row) => row.id !== params.data.id,
      );
      return {
        ...prevData,
        breakChannelTypes: updatedRows,
      };
    });
  };

  const actionCellButtons = (params) => {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          gap: '12px',
        }}
      >
        <img
          src={addButtonIcon}
          alt="Add new row icon"
          title="Add new row"
          onClick={handleAddEditorTableRow}
          style={{
            cursor: 'pointer',
            transform: 'rotate(-45deg)',
            height: '22px',
          }}
        />
        {rowData.length > 1 && (
          <img
            src={deleteIcon}
            alt="Delete Icon"
            title="Delete this"
            onClick={() => handleDeleteRow(params)}
            style={{
              cursor: 'pointer',
            }}
          />
        )}
      </div>
    );
  };

  const switchCellRenderer = (params) => {
    const { colDef, value } = params;
    const { field: fieldName } = colDef;

    const handleSwitchChange = () => {
      setPatternData((prevData) => ({
        ...prevData,
        breakChannelTypes: prevData.breakChannelTypes.map((row) => ({
          ...row,
          [fieldName]: row.id === params.data.id ? !value : row[fieldName],
        })),
      }));
      setDirtyForm(true);
    };

    return (
      <div
        style={{
          display: 'flex',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 'normal',
          fontSize: '15px',
        }}
      >
        <Switch
          text={['No', 'Yes']}
          active={value}
          onSwitch={handleSwitchChange}
          blurredOnInactive={false}
        />
      </div>
    );
  };

  const columnDefinitions = useMemo(
    () => [
      {
        field: 'type',
        headerName: 'Type',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: breakPatternFormOptions.contentTypes,
          allowTyping: true,
          filterList: true,
          valuePlaceholder: 'Select event',
        },
      },
      {
        field: 'slotPosition',
        headerName: 'Segment Position',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: Constants.defaultSegmentPositions,
          allowTyping: true,
          filterList: true,
          valuePlaceholder: 'Select segment position',
        },
      },
      {
        field: 'optional',
        headerName: 'Optional',
        cellRenderer: switchCellRenderer,
        editable: false,
        tooltipValueGetter: () => null,
        filterParams: {
          valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
        }
      },
      {
        field: 'position',
        headerName: 'Position',
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
        },
      },
      {
        field: 'selection',
        headerName: 'Selection',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: Constants.defaultSelectionOptions,
          allowTyping: true,
          filterList: true,
          valuePlaceholder: 'Select selection method',
        },
      },
      {
        field: 'externalID',
        headerName: 'External ID',
      },
      {
        field: 'eventName',
        headerName: 'Event',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: breakPatternFormOptions.events,
          allowTyping: true,
          filterList: true,
          valuePlaceholder: 'Select event',
        },
      },
      {
        field: 'priority',
        headerName: 'Priority',
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
        },
      },
      {
        field: 'action',
        headerName: ' ',
        cellRenderer: actionCellButtons,
        editable: false,
        width: 100,
      },
    ],
    [
      Constants.defaultSegmentPositions,
      breakPatternFormOptions.contentTypes,
      breakPatternFormOptions.events,
      rowData.length,
    ],
  );

  // Returns all fields from columnDefinition except the action column
  const headerFields = columnDefinitions
    .filter((column) => column.field !== 'action')
    .map((column) => column.field);

  const handleGridEditorChange = (params) => {
    const fieldName = params.colDef.field;
    let newValue = params.newValue;

    // Automatically set numeric fields to 0 if left empty
    if (fieldName === 'priority') newValue = params.newValue || 0;

    setPatternData((prevData) => ({
      ...prevData,
      breakChannelTypes: prevData.breakChannelTypes.map((row) => ({
        ...row,
        [fieldName]: row.id === params.data.id ? newValue : row[fieldName],
      })),
    }));

    setDirtyForm(true);
  };

  useEffect(() => {
    setSortingResult((prevState) => ({
      ...prevState,
      ...Object.fromEntries(headerFields.map((field) => [field, false])),
    }));
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <OpDataGrid
          skipGridLoading // WORKAROUND: prevents spinner flash on every cell edit; see BaseGrid updateRowData
          columns={columnDefinitions}
          customVerbiage="No break patterns found"
          editable
          enableMultipleRowSelection={false}
          enableMultipleColumnSorting
          filter
          gridHeightPreset={{ height: 'calc(100% - 60px)' }}
          gridName="break-patterns-editor"
          onCellEditingStopped={handleGridEditorChange}
          resizable
          rows={rowData}
          sortable
          sharedColumnProperties={{
            cellStyle: { fields: headerFields, value: { textAlign: 'left' } },
            flex: { fields: headerFields, value: 1 },
          }}
          showAlternativeRowHighlight
        />
      </div>

      {breakChannelTypeErrors.errorMessage && (
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <small className="error-message">
            {breakChannelTypeErrors.errorMessage}
          </small>
        </div>
      )}

      {/* Temporary error message for row errors */}
      {breakChannelTypeErrors.rowErrors.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <small className="error-message">
            Rows cannot have empty segment or selection values.
          </small>
        </div>
      )}
    </div>
  );
}

export default BreakPatternEditorTable;
